// frontend/src/pages/SupplierSetup.jsx

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import useKeyboardSave from '../hooks/useKeyboardSave';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { useEditableGridState } from '../hooks/useEditableGrid';
import { withSorReadOnly } from '../components/grid/sorColumns';
import { referencePickerColDef } from '../utils/referencePickerColDef';
import { dirtyCellPayload } from '../utils/dirtyCellPayload';
import { formatCurrencyLabel, formatCurrencyCode } from '../utils/itemLabel';
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Box,
} from "@mui/material";
import PageBanner from "../components/common/PageBanner";
import { ICON, Package, Plus, X } from '../icons';

import Tile from '../components/layout/Tile';
import GridIconAction from '../components/grid/GridIconAction';
import { useTabs } from '../tabs/useTabs';
import { useTabParams } from '../tabs/useTabParams';
import { decodeRouteSegment } from '../tabs/routeSegment';
import GridExportButton from '../components/common/GridExportButton';
import FilterRow from '../components/common/FilterRow';
import SearchField from '../components/common/SearchField';
import BulkImportDialog from '../components/common/BulkImportDialog';
import useBulkImport from '../hooks/useBulkImport';
import { DATA } from '../queries/dataChanged';
import ImportButton from '../components/common/ImportButton';
import { useAgGridTheme } from '../utils/agGridTheme';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, actionColumn, iconActionColumn } from '../utils/gridDefaults';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useFormatters } from '../hooks/useFormatters';
import { useGridState } from '../hooks/useGridState';
import { useCountries, useCurrencies } from '../hooks/useReferenceData';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useSsrmGrid } from '../hooks/useSsrmGrid';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SaveDiscardButtons from '../components/common/SaveDiscardButtons';
import CurrencyChangeConfirmModal from '../components/CurrencyChangeConfirmModal';
import { useSupplierBulkUpdate, useSupplierBulkDelete } from '../hooks/useSupplierMutations';
import { translateRequestError } from '../queries/api';
import { ApiError } from '../utils/parseApiError';
import { useAuth, useIsShopify } from '../contexts/AuthContext';
import { supplierCatalogPath } from './supplierCatalogLinks';
import { useActiveErp } from '../hooks/useConnectors';
import { useWriteGate } from '../hooks/useWriteGate';
import LinkSupplierToExactDialog from '../components/settings/LinkSupplierToExactDialog';
import CreateSupplierDialog from '../components/supplier/CreateSupplierDialog';
import VendorSuppliersButton from '../components/supplier/VendorSuppliersButton';
import StatusChip from '../components/common/StatusChip';
import { readGridSelection, NO_SELECTION } from '../utils/gridSelection';
import { describeDelete } from './supplierDeleteImpact';
import { useStorePlanning } from '../hooks/useStorePlanning';
import { orderDayLabel } from './supplierSchedule';
import { failedRowsMessage } from '../utils/failedRows';
import {
  PLANNING_OVERRIDE_FIELDS,
  applyPlanningEdit,
  supplierPlanningColumns,
} from './supplierPlanningColumns';
import '../styles/pages.css';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

// The largest maxLength a textarea accepts (2 ** 31 throws IndexSizeError).
const UNCAPPED_TEXT = 2 ** 31 - 1;
// The two picker columns render SupplierSerializer's read-only nested object;
// the serializer writes them through the `_id` input the picker sets beside it.
const SUPPLIER_WIRE_FIELDS = {
  supplier_country_code: 'supplier_country_code_id',
  default_currency: 'default_currency_id',
};

// Per-row ERP link status / action for the Supplier Setup grid. Shown only
// while an ERP owns supplier master. A native row (no erp_id) offers "Link to
// Exact"; a linked row shows a status chip.
function ErpLinkCell({ data, onLink, t }) {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {data.erp_id ? (
        <StatusChip color="success" label={t('supplierSetup.erpLinked')} />
      ) : (
        <Button
          className="grid-action-btn"
          variant="contained"
          onClick={() => onLink(data)}
        >
          {t('supplierSetup.linkToErp')}
        </Button>
      )}
    </Box>
  );
}

function SupplierSetup() {
  const { t } = useTranslation();
  const { openTab, setOwnTitle } = useTabs();
  const { selectedStore } = useAuth();
  // `/supplier-setup/supplier/:supplier` (supplierSetupLinks.js) opens the
  // grid with the search seeded to one supplier.
  const { supplier: supplierParam } = useTabParams();
  // When an ERP owns supplier master (Domain.SUPPLIERS = ERP → Manexys),
  // suppliers are created in the ERP and synced in. A Manexys-native supplier
  // would have no erp_id and could never be ordered from, so the create/import
  // affordances are withheld. The backend rejects these paths too
  // (supplier_create_erp_managed).
  const canCreateSupplier = useWriteGate('suppliers').mayWrite;
  // Shopify vendors are what the vendor-supplier run reads.
  const isShopify = useIsShopify();
  // The active ERP connector, for the Link-to-ERP column, note and dialog.
  const { connector: activeErp, error: connectorsError } = useActiveErp();
  const erpManaged = activeErp !== null;
  // The native supplier row the merchant is linking to Exact (null = closed).
  const [linkSupplier, setLinkSupplier] = useState(null);
  // Bulk-save dirty tracking via the unified primitives hook. Save flow
  // stays custom because of the currency-change confirmation modal.
  const editing = useEditableGridState({ getRowId: (row) => row.supplier_id });
  const { gridContext, recordCellChange, clearAll: clearAllEdits } = editing;
  const { confirm, dialogProps } = useConfirmDialog();
  const { data: countries, error: countriesError } = useCountries();
  const { data: currencies, error: currenciesError } = useCurrencies();
  const storeCurrencyCode = selectedStore?.currency ?? null;
  // The store's planning defaults — what an inheriting row resolves to. The
  // four override columns stay read-only until this is known (see
  // supplierPlanningColumns), and a failed read is said out loud below.
  const { planning: storePlanning, isError: storePlanningError } = useStorePlanning();
  // Surface a single banner if either reference dataset failed to load.
  const refDataError = countriesError
    ? t('supplierSetup.messages.countriesFailed')
    : currenciesError
      ? t('supplierSetup.messages.currenciesFailed')
      : storePlanningError
        ? t('supplierSetup.messages.storePlanningFailed')
        : null;
  const [selection, setSelection] = useState(NO_SELECTION);
  const bulkDeleteMutation = useSupplierBulkDelete();
  const deleteLoading = bulkDeleteMutation.isPending;
  // Read, not just written: a header-checkbox selection covers every row
  // matching the current search, so the server's filtered total IS the
  // selected count — the loaded pages know nothing about the rest.
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState(() => decodeRouteSegment(supplierParam));
  const [searchText, setSearchText] = useState(() => decodeRouteSegment(supplierParam));
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { parseNumber, formatNumber } = useFormatters();
  const gridState = useGridState('supplier_setup');
  const pageSize = 100;

  const bulkUpdateMutation = useSupplierBulkUpdate();

  const [ccModalOpen, setCcModalOpen] = useState(false);
  const [ccChangeContext, setCcChangeContext] = useState(null);
  const [ccAffectedItems, setCcAffectedItems] = useState([]);
  const [ccPendingPayload, setCcPendingPayload] = useState(null);
  const saving = bulkUpdateMutation.isPending;
  const [ccError, setCcError] = useState(null);

  // Create supplier dialog — the shared CreateSupplierDialog; open/closed is all this page holds.
  const [addDialogOpen, setAddDialogOpen] = useState(false);


  const gridApiRef = useRef(null);

  // Filled by useSsrmGrid below; the discard handler above it re-reads the
  // rows through this.
  const refreshGridRef = useRef(null);

  const exportProps = {
    endpoint: '/api/suppliers/export/',
    filename: 'suppliers',
    getParams: useCallback(
      (scope) => (scope !== 'all' && searchText) ? `&search=${encodeURIComponent(searchText)}` : '',
      [searchText]
    ),
  };

  // Import — supplier upsert by name on the shared import rail.
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const importHook = useBulkImport({
    modelName: 'suppliers',
    endpoint: '/api/suppliers/import/',
    templateEndpoint: '/api/suppliers/template/',
    changes: [DATA.suppliers],
  });
  const importCounters = useMemo(() => [
    { key: 'created', label: (n) => t('bulkImport.resultCreatedCount', { count: n }) },
    { key: 'updated', label: (n) => t('bulkImport.resultUpdatedCount', { count: n }) },
  ], [t]);

  // A drill-through tab names the search it opened on ("Suppliers — Acme"),
  // so two Suppliers tabs are tellable apart in the strip. Keyed on the
  // search, not the route: once the merchant changes it, a title still naming
  // the opening value would be a lie.
  useEffect(() => {
    if (!supplierParam) return;
    setOwnTitle(searchText
      ? t('supplierSetup.filteredTabTitle', { filter: searchText })
      : t('nav.suppliers'));
  }, [supplierParam, searchText, setOwnTitle, t]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Bound to this page's translator; the rules themselves live in
  // supplierSchedule.js, where they are unit-tested.
  const dayLabel = useCallback(
    (dayIndex, reviewFrequency) => orderDayLabel(t, dayIndex, reviewFrequency),
    [t],
  );

  const createColumnDefs = useCallback(() => {
    const planningColumns = supplierPlanningColumns({
      t, formatNumber, parseNumber, dayLabel, planning: storePlanning,
    });
    const _cols = [
      // --- Supplier catalog (every row) ---
      // Its own icon column, not an entry behind a ⋯: this is the grid's
      // most-used drill-through, and one click is what it costs everywhere.
      iconActionColumn({
        colId: 'catalog',
        lockPosition: 'left',
        cellRenderer: GridIconAction,
        cellRendererParams: {
          icon: Package,
          label: t('supplierSetup.actions.catalog'),
          onClick: (row) => openTab(supplierCatalogPath(row.supplier_id)),
        },
      }),
      // --- ERP link (only while an ERP owns supplier master) ---
      ...(erpManaged ? [actionColumn({
        colId: 'erp_link',
        headerName: t('supplierSetup.columns.erpLink'),
        headerTooltip: t('supplierSetup.tooltips.erpLink'),
        lockPosition: 'left',
        cellRenderer: ErpLinkCell,
        cellRendererParams: { onLink: setLinkSupplier, t },
      })] : []),
      // --- Identity ---
      {
        field: "supplier_name",
        headerName: t("supplierSetup.columns.supplierName"),
        editable: true,
      },
      {
        field: "account_number",
        headerName: t("supplierSetup.columns.accountNumber"),
        headerTooltip: t("supplierSetup.tooltips.accountNumber"),
        editable: true,
      },

      // --- Address ---
      {
        field: "supplier_address_line1",
        headerName: t("supplierSetup.columns.address1"),
        editable: true,
      },
      {
        field: "supplier_address_line2",
        headerName: t("supplierSetup.columns.address2"),
        editable: true,
      },
      {
        field: "supplier_city",
        headerName: t("supplierSetup.columns.city"),
        editable: true,
      },
      {
        field: "supplier_state_province",
        headerName: t("supplierSetup.columns.stateProvince"),
        editable: true,
      },
      {
        field: "supplier_postal_code",
        headerName: t("supplierSetup.columns.postalCode"),
        editable: true,
      },
      {
        ...referencePickerColDef({
          field: 'supplier_country_code',
          colId: 'SupplierCountryCode__name',
          options: countries,
          optionId: 'country_code',
          formatLabel: (c) => c.country_name,
          searchableLabels: (c) => [c.country_name, c.country_code],
        }),
        headerName: t("supplierSetup.columns.country"),
        editable: true,
      },

      // --- Contact ---
      {
        field: "supplier_main_contact_name",
        headerName: t("supplierSetup.columns.contactName"),
        editable: true,
      },
      {
        field: "supplier_email",
        headerName: t("supplierSetup.columns.email"),
        editable: true,
      },
      {
        field: "supplier_phone",
        headerName: t("supplierSetup.columns.phone"),
        editable: true,
      },
      {
        field: "website_url",
        headerName: t("supplierSetup.columns.website"),
        editable: true,
      },

      // --- Ordering terms ---
      // Planning overrides (Store → Supplier chain): inventory target pair,
      // lead time, order cadence pair. Effective value shown, user ink when
      // the row overrides, blank / inherit option = null on the wire. Rules
      // and tests in supplierPlanningColumns.js.
      ...planningColumns.inventoryTarget,
      ...planningColumns.leadTime,
      ...planningColumns.orderCadence,
      {
        field: "delivers_on_weekend",
        headerName: t("supplierSetup.columns.weekendDelivery"),
        type: 'booleanColumn',
      },

      // --- Finance ---
      {
        ...referencePickerColDef({
          field: 'default_currency',
          colId: 'DefaultCurrency__CurrencyCode',
          options: currencies,
          optionId: 'currency_code',
          // SupplierSerializer refuses a supplier without a currency.
          required: true,
          // Dropdown shows full info; cell shows compact code+symbol.
          formatLabel: formatCurrencyLabel,
          formatCellLabel: formatCurrencyCode,
          // Paste matches the ISO code only; the list filters on the full
          // label, which already holds it.
          searchableLabels: (c) => [c.currency_code],
        }),
        headerName: t("supplierSetup.columns.defaultCurrency"),
        editable: true,
      },
      {field: "default_tax_rate",
        headerName: t("supplierSetup.columns.defaultTaxRate"),
        editable: true,
        type: 'decimalColumn',
        valueSetter: (params) => {
          if (params.newValue === null || params.newValue === '' || params.newValue === undefined) {
            params.data.default_tax_rate = null;
            return true;
          }
          const val = parseNumber(params.newValue);
          if (isNaN(val) || val < 0 || val > 100) return false;
          params.data.default_tax_rate = val;
          return true;
        },
      },
      // ── FX columns ────────────────────────────────────────────────
      // Locked rate column is the supplier's FX preference: empty cell
      // means "follow the cached ECB rate"; a value means "use this
      // locked rate." Editable whenever supplier ccy differs from store
      // ccy (same-ccy suppliers have nothing to convert). Locked-since
      // is read-only and auto-stamped by Supplier.save() whenever the
      // rate changes.
      {
        field: "fixed_fx_rate",
        headerName: t("supplierSetup.columns.fixedFxRate"),
        headerTooltip: t("supplierSetup.tooltips.fixedFxRate"),
        editable: (params) => (
          params.data.default_currency?.currency_code !== storeCurrencyCode
        ),
        type: 'decimalColumn',
        valueSetter: (params) => {
          if (params.newValue === null || params.newValue === '' || params.newValue === undefined) {
            params.data.fixed_fx_rate = null;
            return true;
          }
          const val = parseNumber(params.newValue);
          if (isNaN(val) || val <= 0) return false;
          params.data.fixed_fx_rate = val;
          return true;
        },
      },
      {
        // Read-only — backend's Supplier.save() auto-stamps this field
        // whenever fixed_fx_rate changes, so the grid never edits it.
        // Empty cell when no lock is set (the dateColumn formatter
        // handles null naturally).
        field: "fixed_fx_rate_set_at",
        headerName: t("supplierSetup.columns.fixedFxRateSetAt"),
        editable: false,
        type: 'dateColumn',
      },

      // --- Notes (free text, large-text editor for multi-line) ---
      // api.models.Supplier.notes / po_notes_template are unbounded TextFields.
      // AG Grid's large-text editor always caps its textarea (200 characters
      // when no maxLength is given), so the cap is the largest the DOM accepts.
      {
        field: "notes",
        headerName: t("supplierSetup.columns.notes"),
        editable: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: { maxLength: UNCAPPED_TEXT, rows: 8, cols: 50 },
      },
      {
        field: "po_notes_template",
        headerName: t("supplierSetup.columns.poNotesTemplate"),
        headerTooltip: t("supplierSetup.tooltips.poNotesTemplate"),
        editable: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: { maxLength: UNCAPPED_TEXT, rows: 8, cols: 50 },
      },
    ];
    // ERP-owned supplier master (Domain.SUPPLIERS) is read-only here when the
    // ERP is SoR — edited in the ERP, mirrors backend SoRReadOnlyMixin. Planning
    // fields (lead time, review cadence, tax, FX lock) stay editable.
    const ERP_OWNED = new Set([
      'supplier_name', 'supplier_address_line1', 'supplier_address_line2',
      'supplier_city', 'supplier_state_province', 'supplier_postal_code',
      'supplier_country_code', 'supplier_main_contact_name', 'supplier_email',
      'supplier_phone', 'website_url', 'default_currency',
    ]);
    return _cols.map((c) =>
      ERP_OWNED.has(c.field) ? withSorReadOnly(c, 'suppliers', t) : c
    );
  }, [countries, currencies, t, dayLabel, parseNumber, formatNumber, storePlanning, erpManaged, storeCurrencyCode, openTab]);

  // Column defs rebuild whenever createColumnDefs's deps change (incl. language via t).
  const columnDefs = useMemo(() => createColumnDefs(), [createColumnDefs]);


  const ssrmParams = useMemo(() => ({
    search: searchText,
  }), [searchText]);

  // delivers_on_weekend arrives as 0/1 from the backend but the grid expects a
  // boolean for the checkbox cell.
  const transformSupplierRows = useCallback((data) => {
    return data.results.map(supplier => ({
      ...supplier,
      delivers_on_weekend: !!supplier.delivers_on_weekend,
    }));
  }, []);

  const handleSsrmData = useCallback((data) => {
    // Every block carries it: /api/suppliers/ uses the default
    // StandardPagination, which always counts.
    setTotalCount(data.count);
  }, []);

  // Stable identity — see ForecastGrid's getRowId for the AG Grid 35.3 rationale.
  const getRowId = useCallback((params) => String(params.data.supplier_id), []);

  const { onGridReady, onGridPreDestroyed, pagination } = useSsrmGrid({
    endpoint: '/api/suppliers/',
    params: ssrmParams,
    pageSize,
    transformRows: transformSupplierRows,
    onData: handleSsrmData,
    onError: (err) => showToast(translateRequestError(err, t, 'common.fetchError'), 'error'),
    gridRef: gridApiRef,
    refreshRef: refreshGridRef,
  });


  const handleSearchChange = async (value) => {
    if (editing.isDirty) {
      const ok = await confirm({
        title: t('common.unsavedChangesTitle'),
        message: t('supplierSetup.messages.unsavedChangesFilter'),
        confirmLabel: t('common.discard'),
        variant: 'warning',
      });
      if (!ok) return;
      clearAllEdits();
    }
    setSearchInput(value);
  };

  // Custom handler — recording goes through the unified primitive, with the
  // planning columns' consequences layered on top (applyPlanningEdit): a
  // cadence change carries its paired order day, and every planning edit
  // re-derives the row's effective_* readings so dependent cells follow in
  // the same render. Each follow-on change is recorded as its own cell entry
  // so the merchant sees the highlight and the saved value matches the
  // displayed one.
  const onCellValueChanged = useCallback((event) => {
    const updatedSupplier = event.data;
    const fieldName = event.colDef.field;
    const oldValue = event.oldValue;
    const newValue = event.newValue;

    recordCellChange({
      row: updatedSupplier,
      fieldName,
      oldValue,
      newValue,
    });

    // The columns are read-only until storePlanning is known, so a planning
    // edit here always has a root to resolve against.
    if (PLANNING_OVERRIDE_FIELDS.includes(fieldName)) {
      applyPlanningEdit({ row: updatedSupplier, field: fieldName, newValue, planning: storePlanning })
        .forEach((change) => recordCellChange({ row: updatedSupplier, ...change }));
    }

    if (event.api) {
      event.api.refreshCells({
        force: true,
        rowNodes: [event.node],
      });
    }
  }, [recordCellChange, storePlanning]);


  // Pull the post-success path out of the bulk-save handler so both
  // the initial save and the currency-change retry share the same
  // success / partial-failure UX. The partial-failure message is the app's
  // one rule for it (utils/failedRows.js).
  const finishBulkSave = (data) => {
    const { summary } = data;
    if (summary.failed > 0) {
      showToast(failedRowsMessage(t, data.results), 'error');
      return;
    }
    showToast(t("supplierSetup.messages.saveSuccess", { count: summary.succeeded }), "success");
    clearAllEdits();
  };

  const handleSaveChanges = async () => {
    const { items } = dirtyCellPayload(editing.getChangedCellsRef(), SUPPLIER_WIRE_FIELDS);
    try {
      const data = await bulkUpdateMutation.mutateAsync({ items });
      // Currency-change confirmation gate: backend returns 200 +
      // requires_confirmation BEFORE saving when any DefaultCurrency edit
      // would reinterpret linked items' costs. We surface the modal and
      // handleCurrencyChangeConfirm retries with the strategy.
      if (data?.requires_confirmation) {
        setCcChangeContext(data.change_context);
        setCcAffectedItems(data.affected_items);
        setCcPendingPayload({ items });
        setCcError(null);
        setCcModalOpen(true);
        return;
      }
      finishBulkSave(data);
    } catch (error) {
      console.error("supplierBulkUpdate:", error.message);
      showToast(translateRequestError(error, t, 'supplierSetup.messages.saveFailed'), 'error');
    }
  };

  const handleCurrencyChangeConfirm = async (confirmBody) => {
    if (!ccPendingPayload) return;
    setCcError(null);
    try {
      const data = await bulkUpdateMutation.mutateAsync({
        items: ccPendingPayload.items,
        confirm: confirmBody,
      });
      setCcModalOpen(false);
      setCcPendingPayload(null);
      setCcChangeContext(null);
      setCcAffectedItems([]);
      finishBulkSave(data);
    } catch (error) {
      if (error instanceof ApiError && error.body?.detail?.message) {
        console.error('currencyChange bulk-update:', error.code, error.body.detail.message);
      } else {
        console.error('currencyChange:', error.message);
      }
      setCcError(translateRequestError(error, t, 'msg.saveFailed'));
    }
  };

  const handleCurrencyChangeCancel = () => {
    setCcModalOpen(false);
    setCcPendingPayload(null);
    setCcChangeContext(null);
    setCcAffectedItems([]);
    setCcError(null);
    showToast(
      t('currencyChange.cancelledAlert'),
      'info',
    );
  };

  useKeyboardSave(handleSaveChanges, editing.isDirty && !saving && !ccModalOpen);
  const unsavedGuard = useUnsavedChangesGuard(editing.isDirty);

  const handleDiscardChanges = () => {
    clearAllEdits();
    if (gridApiRef.current) {
      refreshGridRef.current({ purge: false });
    }
    showToast(t("common.changesDiscarded"), "info");
  };

  const onSelectionChanged = useCallback((event) => {
    // NOT getSelectedRows(): on a server-side grid that returns [] the moment
    // the header checkbox is used, because the selection is a rule over rows
    // that were never loaded. readGridSelection reads the rule.
    setSelection(readGridSelection(event.api, totalCount));
  }, [totalCount]);

  // A select-all is resolved server-side against the search sent WITH the
  // delete, so a selection must not outlive the filter it was made under —
  // "select all" under a search, then clear the search, would otherwise offer
  // to delete rows the merchant never saw. Clearing the GRID's selection is
  // the whole job; it raises onSelectionChanged, which is what resets our
  // copy. Setting both here would be two sources of truth for one fact.
  useEffect(() => {
    gridApiRef.current?.deselectAll();
  }, [searchText]);

  const handleDeleteSelected = async () => {
    if (!selection.payload) return;
    const request = { payload: selection.payload, search: searchText };

    // Step 1 — ask what this would destroy. The endpoint mutates nothing
    // without `confirmed`, so this is a read despite the DELETE verb.
    let preview;
    try {
      preview = await bulkDeleteMutation.mutateAsync(request);
    } catch (error) {
      console.error('supplierBulkDelete preview:', error.message);
      showToast(translateRequestError(error, t, 'supplierSetup.messages.deleteFailed'), "error");
      return;
    }

    if (!preview?.requires_confirmation) {
      // The gate is the only thing standing between a click and a cascade.
      // If it did not answer, do NOT fall through to the confirmed call —
      // surface it instead of guessing what the server meant.
      console.error('supplierBulkDelete: gate did not report a blast radius', preview);
      showToast(t('supplierSetup.messages.deleteFailed'), "error");
      return;
    }

    if (preview.suppliers === 0) {
      showToast(t('supplierSetup.messages.deleteNothingMatched'), "info");
      return;
    }

    // describeDelete throws when the blast radius cannot be stated in full.
    // Refusing here is the point: the merchant is one click from a cascade,
    // and a dialog that under-reports it is worse than no dialog.
    let message;
    try {
      message = describeDelete(preview, t);
    } catch (error) {
      console.error('supplierBulkDelete:', error.message, preview);
      showToast(t('supplierSetup.messages.deleteFailed'), "error");
      return;
    }

    const ok = await confirm({
      title: t('common.deleteTitle'),
      message,
      confirmLabel: t('common.delete'),
      variant: 'danger',
    });
    if (!ok) return;

    // Step 2 — the same selection, now confirmed. Re-resolved server-side, so
    // the rows deleted are the rows matching the filter at THIS moment.
    try {
      const data = await bulkDeleteMutation.mutateAsync({ ...request, confirmed: true });
      showToast(t("supplierSetup.messages.deleteSuccess", { count: data.deleted }), "success");
      setSelection(NO_SELECTION);
      // Clear the grid's own selection state too, else a persisted select-all
      // rule re-selects the rows the reload brings back.
      gridApiRef.current?.deselectAll();
    } catch (error) {
      console.error('supplierBulkDelete:', error.message);
      showToast(translateRequestError(error, t, 'supplierSetup.messages.deleteFailed'), "error");
    }
  };

  return (
    <div className="page-container">
      {unsavedGuard}
      <PageBanner severity="warning" messages={[refDataError]} />
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.suppliers')}
          toolbar={
            <>
              {canCreateSupplier && (
                <Button variant="contained" startIcon={<Plus size={ICON.md} />} onClick={() => setAddDialogOpen(true)}>
                  {t("supplierSetup.createSupplier")}
                </Button>
              )}
              {selection.count > 0 && (
                <Button variant="outlined" color="error" onClick={handleDeleteSelected} loading={deleteLoading}>
                  {`${t("btn.delete")} (${selection.count})`}
                </Button>
              )}
            </>
          }
          editActions={
            <SaveDiscardButtons
              count={editing.changedRows.length}
              saving={saving}
              onSave={handleSaveChanges}
              onDiscard={handleDiscardChanges}
            />
          }
          headerActions={
            <>
              {canCreateSupplier && isShopify && (
                <VendorSuppliersButton />
              )}
              {canCreateSupplier && (
                <ImportButton
                  entityKey="suppliers"
                  onClick={() => setImportDialogOpen(true)}
                />
              )}
              <GridExportButton {...exportProps} />
            </>
          }
        >
          <GridPage>
            <PageBanner severity="error" messages={[connectorsError]} />
            <PageBanner severity="info" messages={[erpManaged && t("supplierSetup.erpManagedNote")]} />
            <FilterRow>
              <SearchField
                className="filter-row-search"
                label={t("supplierSetup.searchLabel")}
                placeholder={t("supplierSetup.searchPlaceholder")}
                value={searchInput}
                onChange={handleSearchChange}
              />
            </FilterRow>

            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  filtersActive={!!searchText}
                >
                  <AgGridReact
                    {...defaultGridProps}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    {...editableGridProps}
                    context={gridContext}
                    theme={gridTheme}
                    {...gridState}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                    defaultColDef={defaultColDef}
                    columnTypes={columnTypes}
                    onCellValueChanged={onCellValueChanged}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    onSelectionChanged={onSelectionChanged}
                    rowModelType="serverSide"
                    serverSideSortAllLevels={true}
                    {...pagination}
                    rowSelection={{
                      mode: 'multiRow',
                      checkboxes: true,
                      headerCheckbox: true,
                      // 'all' is the ONLY honest value here. AG Grid's SSRM docs
                      // (v36, "Selecting All Rows"): 'filtered' and 'currentPage'
                      // "are considered invalid ... the grid will behave as though
                      // rowSelection.selectAll = 'all'".
                      //
                      // 'all' is also what we want: our filter is the server-side
                      // ?search=, so "all rows the datasource would return" IS the
                      // filtered set, and the selection RULE readGridSelection
                      // produces is resolved against that same search.
                      selectAll: 'all',
                      copySelectedRows: false,
                    }}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>
      {/* ── Create supplier dialog ── */}
      {addDialogOpen && (
        <CreateSupplierDialog
          onClose={() => setAddDialogOpen(false)}
          onCreated={() => setAddDialogOpen(false)}
        />
      )}

      {linkSupplier && activeErp && (
        <LinkSupplierToExactDialog
          open
          connectorId={activeErp.id}
          supplier={linkSupplier}
          onClose={() => setLinkSupplier(null)}
          onLinked={() => {
            setLinkSupplier(null);
            showToast(t('supplierSetup.linkedToErp'), 'success');
          }}
        />
      )}

      <ConfirmDialog {...dialogProps} />
      <BulkImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        importHook={importHook}
        entityKey="suppliers"
        // This import can create, so the headline's "X of N applied" is not
        // the whole answer — a merchant needs to know how many of those were
        // new suppliers rather than edits to existing ones.
        counters={importCounters}
      />
      <CurrencyChangeConfirmModal
        open={ccModalOpen}
        changeContext={ccChangeContext}
        affectedItems={ccAffectedItems}
        onConfirm={handleCurrencyChangeConfirm}
        onCancel={handleCurrencyChangeCancel}
        loading={saving}
        errorMessage={ccError}
      />
    </div>
  );
}

export default SupplierSetup;
